// @ts-nocheck
// Bundle widget for the product page. Renders recommended cross-sells
// only (the current product has its own buy button above). Hydrates
// placeholder slots from Shopify's complementary recommendations when
// the merchant hasn't curated picks via beyond.frequently_bought_with.
// Posts checked variants to /cart/add.js as one bundle add.

class FrequentlyBoughtTogether extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId;
    this.max = parseInt(this.dataset.max, 10) || 2;
    this.curated = this.dataset.curated === 'true';
    this.noImageSrc = this.dataset.noImageSrc || '';
    this.moneyFormat = this.#deriveMoneyFormat();

    this.row = this.querySelector('[data-fbt-row]');
    this.totalEl = this.querySelector('[data-fbt-total]');
    this.addBtn = this.querySelector('[data-fbt-add]');

    this.addEventListener('change', this.#onChange);
    this.addBtn.addEventListener('click', this.#onAddBundle);

    if (!this.curated) {
      this.#hydrateFromShopify().catch((err) => {
        console.warn('FBT hydrate failed:', err);
        // Hide the section entirely if recommendations can't be loaded
        this.hidden = true;
      });
    } else {
      this.#updateTotal();
    }
  }

  // Read the money format from the rendered Liquid template (e.g. "$1.00")
  // so JS formatting matches the storefront's currency/locale.
  #deriveMoneyFormat() {
    const tmpl = this.querySelector('[data-fbt-money-template]');
    const sample = (tmpl?.innerHTML || '$1.00').trim();
    const m = sample.match(/^(\D*)([0-9.,]+)(\D*)$/);
    return m
      ? { prefix: m[1], suffix: m[3] }
      : { prefix: '$', suffix: '' };
  }

  #formatMoney(cents) {
    const v = (cents / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${this.moneyFormat.prefix}${v}${this.moneyFormat.suffix}`;
  }

  async #fetchRecs(intent) {
    const url = `/recommendations/products.json?product_id=${this.productId}&intent=${intent}&limit=${this.max}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    return (json.products || []).filter((p) => p.available);
  }

  async #hydrateFromShopify() {
    // Complementary intent is empty until the store accumulates co-purchase
    // history. Fall back to related (vendor/type/tag similarity) so the bundle
    // still surfaces something useful on day one.
    let products = await this.#fetchRecs('complementary');
    if (products.length === 0) products = await this.#fetchRecs('related');

    const slots = [...this.querySelectorAll('[data-fbt-slot]')];
    let used = 0;
    for (let i = 0; i < slots.length; i++) {
      const product = products[i];
      const slot = slots[i];
      if (!product) {
        // Remove unused placeholder + its leading "+"
        slot.previousElementSibling?.remove();
        slot.remove();
        continue;
      }
      const variant = product.variants.find((v) => v.available) || product.variants[0];
      if (!variant) {
        slot.previousElementSibling?.remove();
        slot.remove();
        continue;
      }
      slot.replaceWith(this.#cardFor(product, variant));
      used++;
    }

    if (used === 0) {
      // No usable recs — drop the bundle entirely; the carousel below still serves browsing.
      this.hidden = true;
      return;
    }

    this.#updateTotal();
  }

  #cardFor(product, variant) {
    const label = document.createElement('label');
    label.className = 'fbt__card';
    label.innerHTML = `
      <input
        type="checkbox"
        class="fbt__check"
        data-variant-id="${variant.id}"
        data-price="${variant.price}"
        checked
        aria-label="${this.#escape(product.title)}"
      >
      <a class="fbt__card-media" href="${product.url}" tabindex="-1">
        ${
          product.featured_image
            ? `<img class="fbt__img" loading="lazy" src="${product.featured_image}" alt="">`
            : this.noImageSrc
              ? `<img class="fbt__img fbt__img--fallback" loading="lazy" src="${this.noImageSrc}" alt="${this.#escape(product.title)}">`
              : ''
        }
      </a>
      <span class="fbt__card-body">
        <a class="fbt__card-title" href="${product.url}">${this.#escape(product.title)}</a>
        <span class="fbt__card-price">${this.#formatMoney(variant.price)}</span>
      </span>
    `;
    return label;
  }

  #escape(s) {
    return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  }

  #onChange = (e) => {
    if (e.target.matches('.fbt__check')) this.#updateTotal();
  };

  #updateTotal = () => {
    let cents = 0;
    for (const input of this.querySelectorAll('.fbt__check')) {
      if (input.checked) cents += parseInt(input.dataset.price, 10) || 0;
    }
    this.totalEl.textContent = this.#formatMoney(cents);
  };

  // Collect the section IDs the theme's cart-items components are bound to.
  // Sending these on /cart/add.js asks Shopify to render their updated HTML
  // alongside the cart payload — the drawer and badge listeners read those
  // strings from event.detail.data.sections to refresh in place.
  #collectCartSectionIds() {
    const ids = [];
    for (const el of document.querySelectorAll('cart-items-component')) {
      const sid = el.dataset?.sectionId;
      if (sid && !ids.includes(sid)) ids.push(sid);
    }
    return ids;
  }

  #dispatchCartEvent(eventName, detail) {
    const ev = new Event(eventName, { bubbles: true });
    ev.detail = detail;
    this.dispatchEvent(ev);
  }

  #onAddBundle = async () => {
    const items = [...this.querySelectorAll('.fbt__check')]
      .filter((i) => i.checked)
      .map((i) => ({ id: Number(i.dataset.variantId), quantity: 1 }));

    if (items.length === 0) return;

    const sourceId = this.id || `fbt-${this.productId}`;
    const sectionIds = this.#collectCartSectionIds();
    const itemCount = items.reduce((s, i) => s + i.quantity, 0);

    this.addBtn.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const body = await res.json().catch(() => ({}));

      // Shopify uses `status` (HTTP-like) in the body on cart-add failures.
      if (body.status) {
        this.#dispatchCartEvent('cart:error', {
          sourceId,
          data: { message: body.message, errors: body.errors, description: body.description },
        });
        this.#dispatchCartEvent('cart:update', {
          resource: {},
          sourceId,
          data: { didError: true, source: 'fbt-bundle', itemCount },
        });
        alert(body.description || body.message || 'Could not add the bundle to your cart.');
        return;
      }

      // Fetch the full cart so listeners get a complete resource.
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : undefined))
        .catch(() => undefined);

      this.#dispatchCartEvent('cart:update', {
        resource: cart ?? {},
        sourceId,
        data: {
          source: 'fbt-bundle',
          itemCount,
          productId: String(this.productId),
          sections: body.sections,
        },
      });

      this.#flashAddedState();
    } catch (err) {
      console.warn('FBT bundle add failed:', err);
      alert('Sorry, we could not add the bundle to your cart. Please try again.');
    } finally {
      this.addBtn.removeAttribute('aria-busy');
    }
  };

  #flashAddedState() {
    const original = this._originalBtnText ?? this.addBtn.textContent?.trim() ?? '';
    this._originalBtnText = original;
    this.addBtn.textContent = 'Added to Cart';
    this.addBtn.setAttribute('data-fbt-added', 'true');
    clearTimeout(this._flashTimer);
    this._flashTimer = setTimeout(() => {
      this.addBtn.textContent = original;
      this.addBtn.removeAttribute('data-fbt-added');
    }, 1800);
  }
}

if (!customElements.get('frequently-bought-together')) {
  customElements.define('frequently-bought-together', FrequentlyBoughtTogether);
}
